import React from "react";
import moment from "moment";
import { useHistory } from "react-router-dom";

// @material-ui/core components
import MuiMenuItem from "@material-ui/core/MenuItem";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemIcon from "@material-ui/core/ListItemIcon";

// @material-ui/icons
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CircularProgress from "@material-ui/core/CircularProgress";
import ErrorIcon from "@material-ui/icons/Error";
import { TaskNames, TaskState } from "../../utils/types";
import { isEmpty, multiLineText } from "../../utils/helpers";
import { colorLightGreen, colorSecondary, colorTomatoRed } from "../../utils/colors";
import { grey, red } from "@material-ui/core/colors";

const primaryMessage = task => {
  let meta = JSON.parse(task.meta || "{}");
  meta = meta.planningDto || {};
  let suffix = `for PO Ref: ${meta.poRef}, Colors: ${meta.colors}`;

  const isRunning = [TaskState.NEW, TaskState.WAITING, TaskState.RUNNING].includes(task.state);
  const isCompleted = TaskState.COMPLETED === task.state;
  const isFailed = TaskState.FAILED === task.state;
  const isCancelled = TaskState.CANCELLED === task.state;
  let primary = task.name;
  switch (task.name) {
    case TaskNames.CUT_PLAN_GENERATE:
      primary = isRunning
        ? "Generating Cut Plan"
        : isCompleted
        ? "Cut Plan Generated"
        : isFailed
        ? `Cut Plan Generation Failed ${suffix}`
        : isCancelled
        ? "Cut Plan Generation Cancelled"
        : "";
      break;
    case TaskNames.CUT_PLAN_MAIN_MARKER_UPDATE:
      primary = isRunning
        ? "Updating Main Cut Plan"
        : isCompleted
        ? "Main Cut Plan Updated"
        : isFailed
        ? `Main Cut Plan Update Failed ${suffix}`
        : isCancelled
        ? "Main Cut Plan Updated Cancelled"
        : "";
      break;
    case TaskNames.CUT_PLAN_REMNANT_MARKER_UPDATE:
      primary = isRunning
        ? "Updating Remnant Cut Plan"
        : isCompleted
        ? "Remnant Cut Plan Updated"
        : isFailed
        ? `Remnant Cut Plan Update Failed ${suffix}`
        : isCancelled
        ? "Remnant Cut Plan Updated Cancelled"
        : "";
      break;
    case TaskNames.CUT_PLAN_ADJUST:
      primary = isRunning
        ? "Adjusting Cut Plan"
        : isCompleted
        ? "Cut Plan Adjusted"
        : isFailed
        ? `Cut Plan Adjust Failed ${suffix}`
        : isCancelled
        ? "Cut Plan Adjust Cancelled"
        : "";
      break;
    case TaskNames.CUT_PLAN_MARRY:
      primary = isRunning
        ? "Marrying Cut Plan"
        : isCompleted
        ? "Cut Plan Married"
        : isFailed
        ? `Cut Plan Marry Failed ${suffix}`
        : isCancelled
        ? " Cut Plan Marry Cancelled"
        : "";
      break;
    case TaskNames.CUT_PLAN_RE_GENERATE:
      primary = isRunning
        ? "ReGenerating Remnant Cut Plan"
        : isCompleted
        ? "Remnant Cut Plan ReGenerated"
        : isFailed
        ? `Remnant Cut Plan ReGenerate Failed ${suffix}`
        : isCancelled
        ? "Remnant Cut Plan ReGenerate Cancelled"
        : "";
      break;
    default:
  }
  return primary;
};
const secondaryMessage = (task, translate) => {
  const isFailed = TaskState.FAILED === task.state;
  let message;
  if (isFailed) {
    let error = task.error;
    const idx = error?.indexOf(":");
    if (!isEmpty(idx) && idx !== -1) {
      error = error.substring(idx + 2);
    }
    message = multiLineText(error, 100).join("\n");
  } else {
    let meta = JSON.parse(task.meta || "{}");
    meta = meta.planningDto || {};

    let flowItems = [
      `${translate(`resources.orders.fields.poRef`, { _: "PO Ref" })}: ${meta.poRef}`,
      `${translate(`resources.orders.fields.flowInfo.productName`, { _: "Product" })}: ${meta.productName}`,
      `${translate(`resources.orders.fields.flowInfo.style`, { _: "Style" })}: ${meta.styles}`,
      `${translate(`resources.orders.fields.flowInfo.color`, { _: "PO Ref" })}: ${meta.colors}`
    ];
    if (!isEmpty(meta.inseams)) {
      flowItems.push(`${translate(`resources.orders.fields.flowInfo.inseam`, { _: "Inseam" })}: ${meta.inseams}`);
    }
    if (!isEmpty(meta.destinations)) {
      flowItems.push(`${translate(`resources.orders.fields.flowInfo.destination`, { _: "Destination" })}: ${meta.destinations}`);
    }
    if (!isEmpty(meta.deliveries)) {
      flowItems.push(`${translate(`resources.orders.fields.flowInfo.deliveryDate`, { _: "Delivery Date" })}: ${meta.deliveries}`);
    }
    if (!isEmpty(meta.flowRefs)) {
      flowItems.push(`${translate(`resources.orders.fields.flowInfo.flowRef`, { _: "Flow Ref" })}: ${meta.flowRefs}`);
    }
    if (!isEmpty(meta.customerFlowRefs)) {
      flowItems.push(`${translate(`resources.orders.fields.flowInfo.customerFlowRef`, { _: "Customer Flow Ref" })}: ${meta.customerFlowRefs}`);
    }
    if (meta.noOfLots > 1) {
      flowItems.push(`Lot: ${meta.lot}`);
    }
    if (meta.partCount > 1) {
      flowItems.push(` Part: ${meta.part}`);
    }
    flowItems.push(`By: ${task.createdBy}`);

    message = flowItems.join(", ");
    message = multiLineText(message, 100).join("\n");
  }

  return <span style={{ whiteSpace: "pre", color: isFailed ? red[500] : undefined }}>{message}</span>;
};

const MenuItem = ({ task, isNew = false, divider, translate, onClose }) => {
  let history = useHistory();

  const handleClick = () => {
    const meta = isEmpty(task.meta) ? {} : JSON.parse(task.meta);
    history.push(`/cutPlan/bulk/${task.orderId}/${meta?.planningDto?.cutOrderId}`);

    onClose && onClose();
  };

  return (
    <MuiMenuItem divider={divider} dense={false} style={{ backgroundColor: isNew ? grey[300] : undefined }} onClick={handleClick}>
      <ListItemIcon>
        {task.state === TaskState.COMPLETED ? (
          <CheckCircleIcon style={{ color: colorLightGreen.main }} />
        ) : [TaskState.NEW, TaskState.WAITING, TaskState.RUNNING].includes(task.state) ? (
          <CircularProgress size={24} style={{ color: colorSecondary.main }} />
        ) : (
          <ErrorIcon style={{ color: colorTomatoRed.main }} />
        )}
      </ListItemIcon>
      <ListItemText
        style={{
          marginRight: 52
        }}
        primary={primaryMessage(task)}
        secondary={secondaryMessage(task, translate)}
      />
      <ListItemSecondaryAction>
        <ListItemText
          style={{ textAlign: "right" }}
          primary={
            <span style={{ whiteSpace: "pre" }}>
              {multiLineText(
                moment(task.finishedAt || task.createdAt)
                  .fromNow()
                  .replace("minutes", "mins"),
                12
              ).join("\n")}
            </span>
          }
        />
      </ListItemSecondaryAction>
    </MuiMenuItem>
  );
};

export default MenuItem;
